import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideExperimentalZonelessChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';

import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withViewTransitions,
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
} from '@angular/router';
import { routes } from './app.routes';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { GetInterceptor } from './core/interceptors/get-interceptor.service';
import { SetInterceptor } from './core/interceptors/set-interceptor.service';
import { NgxUiLoaderModule, NgxUiLoaderHttpModule } from 'ngx-ui-loader';
import { MessageService, ConfirmationService } from 'primeng/api';
import { DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import { apiList } from './services/apiList';
import { InitialService } from './services/initial.service';
import { provideServiceWorker } from '@angular/service-worker';
import { provideAteEditor } from '@flogeez/angular-tiptap-editor';
import { shouldRegisterNotificationServiceWorker } from './core/utils/public-route.util';

export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};
const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    provideAteEditor(),
    provideExperimentalZonelessChangeDetection(),
    provideRouter(
      routes,
      withViewTransitions({
        onViewTransitionCreated: ({ transition }) => {
          // Suppress uncaught AbortError when a newer navigation skips the transition
          transition.ready.catch(() => {});
          transition.finished.catch(() => {});
        },
      }),
      withComponentInputBinding(),
      inMemoryScrollingFeature
    ),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      NgxUiLoaderModule,
      NgxUiLoaderHttpModule.forRoot({ showForeground: true })
    ),
    provideAppInitializer(async () => {
      const initialService = inject(InitialService);
      await initialService.getLanguages();
    }),
    ...(shouldRegisterNotificationServiceWorker()
      ? [
          provideServiceWorker('firebase-sw.js', {
            enabled: true,
            registrationStrategy: 'registerWhenStable:3000',
          }),
        ]
      : []),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: GetInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SetInterceptor,
      multi: true,
    },
    TranslateService,
    MessageService,
    apiList,
    ConfirmationService,
    DynamicDialogRef,
    DialogService,
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
  ],
};
